<template>
  <b-modal
    id="modal-apply-leave"
    ref="modal-apply-leave"
    title=""
    ok-title=""
    cancel-title=""
    @hidden="$emit('hidden')"
    size="lg"
    modal-class="attendance-apply-modal apply-leave-modal"
    content-class="attendance-apply-modal-content"
    body-class="p-0"
    hide-footer
    hide-header
    centered
  >
    <div class="leave-request-container">
      <b-row class="no-gutters">
        <!-- Left Side: Form -->
        <b-col lg="7" md="12" cols="12" class="leave-form-section order-1">
          <div class="leave-form-content">
            <!-- Header -->
            <div class="modal-flow-header">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="modal-flow-icon bg-light-primary">
                    <feather-icon icon="CalendarIcon" size="18" class="text-primary" />
                  </div>
                  <div>
                    <h5 class="mb-0 font-weight-bolder">Apply Leave</h5>
                    <small class="text-muted">Select dates on the calendar</small>
                  </div>
                </div>
                <b-button variant="flat-secondary" class="btn-icon p-50" @click="hide">
                  <feather-icon icon="XIcon" size="20" />
                </b-button>
              </div>

              <!-- Compact leave balance -->
              <div class="balance-strip mt-2">
                <div class="d-flex align-items-center flex-wrap">
                  <b-badge pill class="balance-total-badge mr-1 mb-50">
                    {{ totalLeaveRemaining }} remaining
                  </b-badge>
                  <b-badge
                    v-for="(item, idx) in availableLeaveBreakdown"
                    :key="idx"
                    pill
                    :class="['balance-chip', 'mb-50', 'mr-50', Number(item.value) > 0 ? 'has-balance' : 'zero-balance']"
                  >
                    {{ shortLeaveLabel(item.label) }}
                    <span class="font-weight-bolder ml-25">{{ formatBalance(item.value) }}</span>
                  </b-badge>
                </div>
              </div>
            </div>

            <!-- Employee Selection (for team view) -->
            <b-form-group
              v-if="(viewMode === 'team' || viewMode === 'org') && canManageLeaves && canApplyOnBehalf"
              label="Apply on behalf of"
              label-for="selected-employee-leave"
              class="mb-2"
            >
              <b-form-select
                id="selected-employee-leave"
                :value="leaveForm.selected_employee"
                :options="getEmployeeOptionsForLeave"
                @change="$emit('update:leave-form', { ...leaveForm, selected_employee: $event }); $emit('handle-leave-employee-change', $event)"
              >
                <template #first>
                  <option :value="null">Myself (default)</option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-alert v-if="isOnProbation" variant="warning" show class="mb-2 py-1 px-2 compact-alert">
              <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
              On probation — HR will decide paid/unpaid.
            </b-alert>

            <b-alert v-if="managerAbsent && !isOnProbation" variant="info" show class="mb-2 py-1 px-2 compact-alert">
              <feather-icon icon="InfoIcon" size="14" class="mr-50" />
              Manager on leave — request routes to HR.
            </b-alert>

            <b-form>
              <!-- Date summary -->
              <div class="date-summary-row mb-2">
                <div class="date-summary-cell">
                  <label class="date-summary-label" for="from-date">From</label>
                  <b-form-input
                    id="from-date"
                    :value="leaveForm.from_date ? formatDateForDisplay(leaveForm.from_date) : ''"
                    readonly
                    class="bg-light date-summary-input"
                    placeholder="—"
                  />
                </div>
                <div class="date-summary-sep d-none d-md-flex">
                  <feather-icon icon="ArrowRightIcon" size="14" class="text-muted" />
                </div>
                <div class="date-summary-cell">
                  <label class="date-summary-label" for="to-date">To</label>
                  <b-form-input
                    id="to-date"
                    :value="leaveForm.to_date ? formatDateForDisplay(leaveForm.to_date) : ''"
                    readonly
                    class="bg-light date-summary-input"
                    placeholder="—"
                  />
                </div>
                <div class="date-summary-cell date-summary-days">
                  <label class="date-summary-label" for="days">Days</label>
                  <b-form-input
                    id="days"
                    :value="calculatedLeaveDays"
                    readonly
                    class="bg-light font-weight-bold date-summary-input text-center"
                  />
                </div>
              </div>
              <small class="text-muted d-block mb-2 date-hint">
                <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                Click start &amp; end dates on the calendar
              </small>

              <!-- Leave Type -->
              <b-form-group label="Leave type" label-for="leave-type" class="mb-2">
                <b-form-select
                  id="leave-type"
                  :value="leaveForm.leave_type"
                  :options="filteredLeaveTypeOptions"
                  required
                  @change="$emit('update:leave-form', { ...leaveForm, leave_type: $event }); $emit('handle-leave-type-change', $event)"
                />
              </b-form-group>

              <!-- Half Day or Full Day Selection -->
              <b-form-group
                v-if="selectedLeaveTypeSupportsHalfDay"
                label="Duration"
                label-for="leave-duration"
                class="mb-2"
              >
                <b-form-radio-group
                  id="leave-duration"
                  :checked="leaveForm.is_half_day"
                  :options="[
                    { value: false, text: 'Full Day' },
                    { value: true, text: 'Half Day' }
                  ]"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  required
                  @change="$emit('update:leave-form', { ...leaveForm, is_half_day: $event }); $emit('handle-leave-duration-change', $event)"
                />
              </b-form-group>

              <!-- Half Day Type -->
              <b-form-group
                v-if="leaveForm.is_half_day === true"
                label="Select half"
                label-for="half-day-type"
                class="mb-2"
              >
                <b-form-radio-group
                  id="half-day-type"
                  :checked="leaveForm.half_day_type"
                  :options="[
                    { value: '1st Half Day', text: '1st Half' },
                    { value: '2nd Half Day', text: '2nd Half' }
                  ]"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  required
                  @change="$emit('update:leave-form', { ...leaveForm, half_day_type: $event })"
                />
              </b-form-group>

              <!-- Comment -->
              <b-form-group label="Comment" label-for="leave-reason" class="mb-2">
                <b-form-textarea
                  id="leave-reason"
                  :value="leaveForm.comment"
                  rows="2"
                  placeholder="Reason for leave"
                  required
                  @input="$emit('update:leave-form', { ...leaveForm, comment: $event })"
                />
              </b-form-group>

              <!-- Footer actions -->
              <div class="modal-flow-footer">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="$emit('save-as-draft')"
                >
                  <feather-icon icon="EditIcon" size="14" class="mr-50" />
                  Save draft
                </b-button>
                <b-button
                  variant="primary"
                  class="btn-attendance-primary"
                  @click="$emit('handle-apply-leave')"
                >
                  <feather-icon icon="SendIcon" size="14" class="mr-50" />
                  Send request
                </b-button>
              </div>
            </b-form>
          </div>
        </b-col>

        <!-- Right Side: Calendar -->
        <b-col lg="5" md="12" cols="12" class="leave-calendar-section order-2">
          <div class="leave-calendar-content">
            <div class="calendar-header mb-2">
              <div class="d-flex justify-content-between align-items-center">
                <b-button variant="flat-primary" class="btn-icon p-25" @click="$emit('navigate-calendar-month', -1)">
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </b-button>
                <h6 class="mb-0 font-weight-bolder calendar-month-label">{{ displayCalendarMonthYear }}</h6>
                <b-button variant="flat-primary" class="btn-icon p-25" @click="$emit('navigate-calendar-month', 1)">
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </b-button>
              </div>
            </div>
            <div class="calendar-wrapper">
              <div class="calendar-grid">
                <div class="calendar-weekdays">
                  <div v-for="(day, idx) in weekDayLabels" :key="`weekday-${idx}`" class="weekday">{{ day }}</div>
                </div>
                <div class="calendar-days">
                  <div
                    v-for="(day, index) in leaveCalendarDays"
                    :key="`day-${day.dateStr}-${index}`"
                    class="calendar-day-item"
                    :class="getCalendarDayClasses(day)"
                    :title="day.isDisabled ? 'Unavailable' : ''"
                    @click="!day.isDisabled && $emit('select-calendar-date', day)"
                  >
                    {{ day.date }}
                  </div>
                </div>
              </div>
            </div>
            <div class="calendar-legend mt-2">
              <span class="legend-item"><span class="legend-swatch available" /> Available</span>
              <span class="legend-item"><span class="legend-swatch unavailable" /> Unavailable</span>
              <span class="legend-item"><span class="legend-swatch selected" /> Selected</span>
            </div>
            <div v-if="leaveForm.from_date || leaveForm.to_date" class="text-center mt-2">
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="$emit('clear-date-selection')"
              >
                <feather-icon icon="XIcon" size="12" class="mr-25" />
                Clear dates
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormSelect,
  BFormSelectOption,
  BFormInput,
  BFormRadioGroup,
  BFormTextarea,
  BButton,
  BAlert,
  BBadge,
} from 'bootstrap-vue'

export default {
  name: 'LeaveApplicationModal',
  components: {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BFormInput,
    BFormRadioGroup,
    BFormTextarea,
    BButton,
    BAlert,
    BBadge,
  },
  props: {
    leaveForm: {
      type: Object,
      required: true,
    },
    viewMode: {
      type: String,
      default: 'my',
    },
    canManageLeaves: {
      type: Boolean,
      default: false,
    },
    canApplyOnBehalf: {
      type: Boolean,
      default: false,
    },
    displayKPIStats: {
      type: Object,
      default: () => ({ leaveBalance: 0 }),
    },
    availableLeaveBreakdown: {
      type: Array,
      default: () => [],
    },
    getEmployeeOptionsForLeave: {
      type: Array,
      default: () => [],
    },
    isOnProbation: {
      type: Boolean,
      default: false,
    },
    managerAbsent: {
      type: Boolean,
      default: false,
    },
    filteredLeaveTypeOptions: {
      type: Array,
      default: () => [],
    },
    selectedLeaveTypeSupportsHalfDay: {
      type: Boolean,
      default: false,
    },
    displayCalendarMonthYear: {
      type: String,
      default: '',
    },
    weekDayLabels: {
      type: Array,
      default: () => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    },
    leaveCalendarDays: {
      type: Array,
      default: () => [],
    },
    calculatedLeaveDays: {
      type: [Number, String],
      default: 0,
    },
    formatDateForDisplay: {
      type: Function,
      required: true,
    },
    getCalendarDayClasses: {
      type: Function,
      required: true,
    },
  },
  computed: {
    totalLeaveRemaining() {
      const fromKpi = this.displayKPIStats && this.displayKPIStats.leaveBalance
      if (fromKpi != null && fromKpi !== '') {
        return this.formatBalance(fromKpi)
      }
      const sum = (this.availableLeaveBreakdown || []).reduce(
        (acc, item) => acc + (parseFloat(item.value) || 0),
        0,
      )
      return this.formatBalance(sum)
    },
  },
  methods: {
    hide() {
      this.$refs['modal-apply-leave'].hide()
    },
    show() {
      this.$refs['modal-apply-leave'].show()
    },
    formatBalance(val) {
      const n = parseFloat(val)
      if (Number.isNaN(n)) return '0'
      return Number.isInteger(n) ? String(n) : n.toFixed(1)
    },
    shortLeaveLabel(label) {
      if (!label) return 'Leave'
      const map = {
        'Casual Leave': 'CL',
        'Sick Leave': 'SL',
        'Earned Leave': 'EL',
        'Privilege Leave': 'PL',
        'Compensatory Off': 'CO',
        'Maternity Leave': 'ML',
        'Paternity Leave': 'PTL',
      }
      if (map[label]) return map[label]
      const words = String(label).trim().split(/\s+/)
      if (words.length === 1) return words[0].slice(0, 4)
      return words.map(w => w[0]).join('').slice(0, 4).toUpperCase()
    },
  },
}
</script>

<style lang="scss" scoped>
$att-teal: #0099AC;
$att-teal-dark: #007a8a;
$att-border: #e5e7eb;
$att-bg-soft: #f3fafb;

.leave-request-container {
  min-height: 280px;
  max-height: 78vh;
  overflow-y: auto;
}

.leave-form-section {
  padding: 1rem 1.1rem 0.85rem;
  background: #fff;
  border-right: 1px solid $att-border;
}

.leave-calendar-section {
  padding: 1rem;
  background: $att-bg-soft;
}

.modal-flow-header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid $att-border;
}

.modal-flow-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.65rem;
  background: rgba(0, 153, 172, 0.12) !important;
  color: $att-teal;
}

.balance-strip {
  background: #f8fafb;
  border: 1px solid $att-border;
  border-radius: 8px;
  padding: 0.4rem 0.55rem;
}

.balance-total-badge {
  background: $att-teal !important;
  color: #fff !important;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.35em 0.7em;
}

.balance-chip {
  font-size: 0.68rem;
  font-weight: 500;
  padding: 0.3em 0.55em;
  border: 1px solid transparent;
}

.balance-chip.has-balance {
  background: rgba(0, 153, 172, 0.1) !important;
  color: $att-teal-dark !important;
  border-color: rgba(0, 153, 172, 0.25);
}

.balance-chip.zero-balance {
  background: #f1f3f5 !important;
  color: #9aa0a6 !important;
  border-color: #e9ecef;
}

.compact-alert {
  font-size: 0.8rem;
  line-height: 1.35;
}

.date-summary-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.date-summary-cell {
  flex: 1 1 0;
  min-width: 96px;
}

.date-summary-days {
  flex: 0 0 72px;
  min-width: 64px;
  max-width: 80px;
}

.date-summary-sep {
  align-items: center;
  padding-bottom: 0.55rem;
}

.date-summary-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.date-summary-input {
  font-size: 0.85rem;
  min-height: 36px;
}

.date-hint {
  font-size: 0.72rem;
}

.modal-flow-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid $att-border;
}

.btn-attendance-primary {
  background: $att-teal !important;
  border-color: $att-teal !important;
  color: #fff !important;
  font-weight: 600;
}

.btn-attendance-primary:hover,
.btn-attendance-primary:focus {
  background: $att-teal-dark !important;
  border-color: $att-teal-dark !important;
}

.calendar-header {
  padding: 0.4rem 0.5rem;
  background: #fff;
  border: 1px solid $att-border;
  border-radius: 8px;
}

.calendar-month-label {
  color: $att-teal-dark;
  font-size: 0.9rem;
}

.calendar-wrapper {
  display: flex;
  justify-content: center;
}

.calendar-grid {
  width: 100%;
  max-width: 260px;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.2rem;
  margin-bottom: 0.35rem;
}

.weekday {
  text-align: center;
  font-weight: 600;
  font-size: 0.7rem;
  color: #6c757d;
  padding: 0.2rem 0;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.2rem;
}

.calendar-day-item {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.78rem;
  position: relative;
  color: #374151;
  min-height: 30px;
  border: 1px solid transparent;
  background: #fff;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.calendar-day-item:hover:not(.other-month):not(.selected-start):not(.selected-end):not(.disabled) {
  background: rgba(0, 153, 172, 0.1);
  border-color: rgba(0, 153, 172, 0.35);
}

.calendar-day-item.selected-range {
  background: rgba(0, 153, 172, 0.18);
  border-color: rgba(0, 153, 172, 0.3);
  color: $att-teal-dark;
}

.calendar-day-item.selected-start,
.calendar-day-item.selected-end {
  background: $att-teal;
  border-color: $att-teal;
  color: #fff;
}

.calendar-day-item.other-month {
  opacity: 0.35;
  cursor: default;
}

.calendar-day-item.today:not(.selected-start):not(.selected-end):not(.disabled) {
  border-color: $att-teal;
  color: $att-teal-dark;
}

.calendar-day-item.disabled {
  cursor: not-allowed;
  background: #f3f4f6;
  color: #9ca3af;
  opacity: 1;
}

.calendar-day-item.disabled::after {
  content: '';
  position: absolute;
  left: 18%;
  right: 18%;
  top: 50%;
  height: 1.5px;
  background: rgba(156, 163, 175, 0.85);
  transform: rotate(-28deg);
  pointer-events: none;
}

.calendar-day-item.holiday-disabled:not(.selected-start) {
  background: #fff8e6;
  color: #a16207;
}

.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.55rem;
  font-size: 0.68rem;
  color: #6b7280;
}

.legend-item {
  display: inline-flex;
  align-items: center;
}

.legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 0.3rem;
  display: inline-block;
  border: 1px solid transparent;
}

.legend-swatch.available {
  background: #fff;
  border-color: #d1d5db;
}

.legend-swatch.unavailable {
  background: #f3f4f6;
  position: relative;
  box-shadow: inset 0 0 0 1px #d1d5db;
}

.legend-swatch.selected {
  background: $att-teal;
}

@media (max-width: 991.98px) {
  .leave-form-section {
    border-right: none;
    border-bottom: 1px solid $att-border;
  }

  .leave-calendar-section {
    border-top: none;
  }
}
</style>

<!-- b-modal teleports to body; unscoped so frame radius applies -->
<style lang="scss">
.attendance-apply-modal.apply-leave-modal .modal-content,
.attendance-apply-modal.apply-leave-modal .attendance-apply-modal-content {
  border-radius: 0.75rem;
  overflow: hidden;
}
</style>
